import { Database } from "@cayo/shared/api/types/cayo-graph";
import HttpStatus from "@cayo/shared/api/types/HttpStatus";
import { IShowControlHandler } from "@cayo/shared/components/App/types";
import { appSignals } from "@cayo/shared/components/App/utils";
import { showError } from "@cayo/shared/components/App/utils/showError";
import logger, { ILogger } from "@cayo/shared/libs/logger";
import { actionHandlers } from "@cayo/shared/scheme/actions";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { settingsEndpoints } from "../../../api/settings-endpoints";
import ajax from "@cayo/shared/libs/ajax";

export type configurationActions = {
  onAddTenant: () => void;
  onAddDomain: () => void;
  onActivate: () => void;
  onConfigureDatabase: () => void;
  onConfigureLater: () => void;
};

export type configInfo = {
  hasDomains?: boolean;
  hasTenants?: boolean;
  hasLicense?: boolean;
  databaseConfigured?: boolean;
  useLegacyLicensing?: boolean;
};

export interface ProductConfigurationSummary {
  hasTenants: boolean;
  hasDomains: boolean;
  hasLicenses: boolean;
  hasActiveLicenses: boolean;
  useLegacyLicensing: boolean;
  isCurrentUserAdmin: boolean;
  historyDatabaseConfig: {
    serverName: string;
    databaseName: string;
    databasePath: string;
    loginName: string;
    isLocal: boolean;
  };
}

type UserSettings = { showInitialWizard?: boolean; databaseConfigured?: boolean };

class InitialConfigurationController {
  private _isWizardVisible: boolean | undefined = undefined;
  private hasDomains: boolean | undefined = undefined;
  private hasTenants: boolean | undefined = undefined;
  private hasLicense: boolean | undefined = undefined;
  private databaseConfigured: boolean | undefined = undefined;
  private useLegacyLicensing?: boolean;
  private log: ILogger = logger.getLogger("InitialConfigurationController");

  constructor(
    readonly showControl: (prms: IShowControlHandler) => void,
    readonly onRefresh: () => void
  ) {}

  private _storageSettings: Database | undefined;

  public async init() {
    let showConfigWizard: boolean | undefined;
    let isDbLocal: boolean | undefined;
    let userSettings: UserSettings | undefined;

    try {
      const { data } = await ajax.getClient(settingsEndpoints.uiSettings);

      userSettings = data ? JSON.parse(data) : {};
      this.log.info("init, settings", userSettings);

      showConfigWizard = userSettings?.showInitialWizard ?? undefined;
    } catch (e: any) {
      if (e?.status !== HttpStatus.NOT_FOUND) {
        const error = await ajaxUtils.getError(e);
        showError(error?.error_description);
        return;
      }
    }

    if (showConfigWizard === false) {
      this._isWizardVisible = false;
      return;
    }

    try {
      this.databaseConfigured = userSettings?.databaseConfigured;

      const {
        hasTenants,
        hasDomains,
        hasLicenses,
        useLegacyLicensing,
        isCurrentUserAdmin,
        historyDatabaseConfig,
      } = (await ajax.getClient(
        `${endpoints.PUBLIC_URL}/v0/userInterface/getProductConfigSummary`
      )) as ProductConfigurationSummary;

      if (!isCurrentUserAdmin) {
        this._isWizardVisible = false;
        return;
      }

      this.hasTenants = hasTenants;
      this.hasDomains = hasDomains;
      this.hasLicense = hasLicenses;
      this.useLegacyLicensing = useLegacyLicensing;

      this._storageSettings = {
        ...historyDatabaseConfig,
        server: historyDatabaseConfig.serverName,
      } as Database;

      isDbLocal = historyDatabaseConfig.isLocal;
      this.log.info("init, storage settings", this._storageSettings);
    } catch (e) {
      await ajaxUtils.handleError(e);
    } finally {
      if (!isDbLocal) {
        this.databaseConfigured = true;
      }
    }

    this._isWizardVisible = showConfigWizard ?? undefined;

    if (this._isWizardVisible === undefined) {
      this._isWizardVisible = !this.hasTenants || !this.hasDomains || !this.hasLicense;
    }
  }

  public get wizardVisible() {
    return this._isWizardVisible;
  }
  public get storageSettings() {
    return this._storageSettings;
  }

  private saveSettings = async (newSettings: UserSettings) => {
    const result = await ajax.getClient(settingsEndpoints.uiSettings, "PATCH", undefined, {
      data: JSON.stringify(newSettings),
    });
    return result;
  };

  private configureLater = async () => {
    try {
      const newSettings: UserSettings = {
        showInitialWizard: false,
        databaseConfigured: this.databaseConfigured,
      };

      this.log.info("configureLater, saving settings", newSettings);

      const savedSettings = await this.saveSettings(newSettings);

      this.log.info("configureLater, saved settings", savedSettings);

      this._isWizardVisible = false;
      this.onRefresh();
    } catch (e) {
      await ajaxUtils.handleError(e);
    }
  };

  private useBuiltinDatabase = async () => {
    try {
      const newSettings: UserSettings = {
        databaseConfigured: true,
        showInitialWizard: this._isWizardVisible,
      };

      this.log.info("useBuiltinDatabase, saving settings", newSettings);

      const savedSettings = await this.saveSettings(newSettings);

      this.log.info("useBuiltinDatabase, saved settings", savedSettings);

      this.databaseConfigured = true;
      this.onRefresh();
    } catch (e) {
      await ajaxUtils.handleError(e);
    }
  };

  public showWizard = async () => {
    try {
      const newSettings: UserSettings = {
        showInitialWizard: true,
        databaseConfigured: this.databaseConfigured,
      };

      this.log.info("showWizard, saving settings", newSettings);

      const savedSettings = await this.saveSettings(newSettings);

      this.log.info("showWizard, saved settings", savedSettings);

      this._isWizardVisible = true;
      this.onRefresh();
    } catch (e) {
      await ajaxUtils.handleError(e);
    }
  };

  public get hasManagedSystems() {
    return this.hasDomains === undefined || this.hasTenants === undefined
      ? undefined
      : this.hasDomains || this.hasTenants;
  }

  public get configuration() {
    const onRefresh = this.onRefresh;
    return {
      configInfo: {
        hasDomains: this.hasDomains,
        hasTenants: this.hasTenants,
        hasLicense: this.hasLicense,
        databaseConfigured: this.databaseConfigured,
        useLegacyLicensing: this.useLegacyLicensing,
      } as configInfo,

      actions: {
        onAddTenant: () =>
          this.showControl({
            controlId: "NewManagedTenantWizard",
            folder: "cayo.ui.wizards",
            onRefresh,
            reloadAfterClose: true,
          }),
        onAddDomain: () =>
          this.showControl({
            controlId: "NewManagedPartitionWizard",
            folder: "cayo.ui.wizards",
            onRefresh,
            reloadAfterClose: true,
            parameters: {
              isInitial: true,
            },
          }),
        onActivate: () => {
          if (this.useLegacyLicensing) {
            this.showControl({
              controlId: "ActivationWizard",
              folder: "cayo.ui.wizards",
              onRefresh,
              reloadAfterClose: true,
            });
          } else {
            actionHandlers["show.wizard"]("product-activation,reloadAfterClose");
          }
        },
        onConfigureDatabase: () =>
          appSignals.send("showClientWizard", {
            schemeId: "DatabaseConfigurationWizard",
            reloadAfterClose: false,
            parameters: {
              databaseSettings: this._storageSettings,
              onUseBuiltinDatabase: this.useBuiltinDatabase,
            },
          }),
        onConfigureLater: this.configureLater,
      } as configurationActions,
    };
  }
}

export default InitialConfigurationController;
